import React, { createContext, useContext, useEffect, useState } from 'react';

import dayjs from 'dayjs';

let lastClearTimestamp = dayjs();

export const useClearLoadedImagesOnUnmount = () => {
  const { clearAllLoadedImages, areImagesCleared } = useLoadedImages();

  useEffect(() => {
    return () => {
      const currentTime = dayjs();
      if (
        !areImagesCleared &&
        currentTime.diff(lastClearTimestamp, 'minute') >= 2
      ) {
        clearAllLoadedImages();
        lastClearTimestamp = currentTime;
      }
    };
  }, [clearAllLoadedImages, areImagesCleared]);
};

interface LoadedImageContextProps {
  loadedImages: Set<string>;
  setLoadedImage: (src: string) => void;
  clearAllLoadedImages: () => void;
  areImagesCleared: boolean;
}

const LoadedImageContext = createContext<LoadedImageContextProps | undefined>(
  undefined,
);

export const LoadedImageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [areImagesCleared, setAreImagesCleared] = useState(false);

  const setLoadedImage = (src: string) => {
    setLoadedImages((prevLoadedImages) => new Set(prevLoadedImages.add(src)));
    setAreImagesCleared(false);
  };

  const clearAllLoadedImages = () => {
    setLoadedImages(new Set());
    setAreImagesCleared(true);
  };

  return (
    <LoadedImageContext.Provider
      value={{
        loadedImages,
        setLoadedImage,
        clearAllLoadedImages,
        areImagesCleared,
      }}
    >
      {children}
    </LoadedImageContext.Provider>
  );
};

export const useLoadedImages = () => {
  const context = useContext(LoadedImageContext);
  if (!context) {
    throw new Error(
      'useLoadedImages must be used within a LoadedImageProvider',
    );
  }
  return context;
};
